export const allowedSetTypes = ['ST', 'P', 'OP', 'EB', 'PRB']

export function normalizeCardStorage(rawStorage) {
  if (!rawStorage) return {}
  if (typeof rawStorage === 'object') return { ...rawStorage }
  if (typeof rawStorage === 'string') {
    try {
      const parsed = JSON.parse(rawStorage)
      if (parsed && typeof parsed === 'object') return { ...parsed }
    } catch {}
  }
  return {}
}

export function normalizeSetType(setType, cardId = '') {
  const normalized = (setType || '').trim().toUpperCase()
  if (normalized) {
    if (allowedSetTypes.includes(normalized)) return normalized
    // Handle values like 'EB01' → 'EB' by stripping trailing digits
    const stripped = normalized.replace(/\d+$/, '')
    if (stripped && allowedSetTypes.includes(stripped)) return stripped
    return normalized
  }
  // Fallback: derive from card ID prefix (e.g. 'EB01-001' → 'EB01' → 'EB')
  const [prefix = ''] = (cardId || '').trim().toUpperCase().split('-')
  if (!prefix) return ''
  if (allowedSetTypes.includes(prefix)) return prefix
  const strippedPrefix = prefix.replace(/\d+$/, '')
  if (strippedPrefix && allowedSetTypes.includes(strippedPrefix)) return strippedPrefix
  return prefix
}

export function buildFullSetId(setType, setId) {
  const t = (setType || '').trim().toUpperCase()
  const s = (setId || '').trim().toUpperCase().replace(/[-\s]+/g, '')
  if (!t || !s || t === 'P') return s
  return s.startsWith(t) ? s : t + s
}

export function composeCardId(setType, setId, cardNumber) {
  const normalizedSetType = (setType || '').trim().toUpperCase()
  let normalizedSetId = (setId || '').trim().toUpperCase().replace(/\s+/g, '')
  const normalizedCardNumber = (cardNumber || '').trim().toUpperCase().replace(/\s+/g, '')
  if (!normalizedSetType || !normalizedCardNumber) return ''
  if (normalizedSetId.startsWith(`${normalizedSetType}-`)) {
    normalizedSetId = normalizedSetId.slice(normalizedSetType.length + 1)
  } else if (normalizedSetId.startsWith(normalizedSetType)) {
    normalizedSetId = normalizedSetId.slice(normalizedSetType.length)
  }
  return `${normalizedSetType}${normalizedSetId}-${normalizedCardNumber}`
}

export function getCardApiBaseBySetType(setType) {
  if (!allowedSetTypes.includes(setType)) return null
  if (setType === 'ST') return 'decks'
  if (setType === 'P') return 'promos'
  if (setType === 'OP' || setType === 'EB' || setType === 'PRB') return 'sets'
  return null
}

export function normalizeApiString(value) {
  if (Array.isArray(value)) return value.join(' / ')
  if (value === undefined || value === null) return ''
  return String(value)
}

export function normalizeApiPrice(value) {
  if (value === undefined || value === null || value === '') return ''
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : ''
}

export function normalizeApiCardNumber(cardData, currentValue = '') {
  const explicitCardNumber = cardData?.card_number
  if (explicitCardNumber !== undefined && explicitCardNumber !== null && explicitCardNumber !== '') {
    return String(explicitCardNumber).trim()
  }
  const fallbackNumber = cardData?.number
  if (fallbackNumber === undefined || fallbackNumber === null || fallbackNumber === '') return ''
  const fallbackString = String(fallbackNumber).trim()
  const currentString = String(currentValue ?? '').trim()
  if (
    currentString &&
    currentString.length > fallbackString.length &&
    currentString.replace(/^0+/, '') === fallbackString.replace(/^0+/, '')
  ) {
    return currentString
  }
  return fallbackString
}

export function normalizeApiSetId(rawSetId, setType = '', cardNumber = '') {
  let normalizedSetId = normalizeApiString(rawSetId).trim().toUpperCase().replace(/\s+/g, '')
  const normalizedCardNumber = String(cardNumber || '').trim().toUpperCase().replace(/\s+/g, '')
  const normalizedSetType = String(setType || '').trim().toUpperCase()
  if (!normalizedSetId) return ''
  if (normalizedSetType === 'P') return ''
  while (normalizedCardNumber && normalizedSetId.endsWith(`-${normalizedCardNumber}`)) {
    normalizedSetId = normalizedSetId.slice(0, -(normalizedCardNumber.length + 1))
  }
  if (normalizedSetType && normalizedSetId.startsWith(`${normalizedSetType}-`)) {
    normalizedSetId = normalizedSetId.slice(normalizedSetType.length + 1)
  } else if (normalizedSetType && normalizedSetId.startsWith(normalizedSetType)) {
    normalizedSetId = normalizedSetId.slice(normalizedSetType.length)
  }
  normalizedSetId = normalizedSetId.replace(/^-+/, '')
  if (normalizedSetType && normalizedSetId === normalizedSetType) return ''
  return normalizedSetId
}

export function normalizeApiCardId(rawCardId) {
  return normalizeApiString(rawCardId).trim().toUpperCase().replace(/\s+/g, '')
}

export function getFetchOptionLabel(cardData, index) {
  const name = normalizeApiString(cardData?.card_name || cardData?.name) || 'Unnamed card'
  const rarity = normalizeApiString(cardData?.rarity)
  const cardType = normalizeApiString(cardData?.card_type)
  const color = normalizeApiString(cardData?.card_color)
  const cardValue = normalizeApiString(cardData?.market_price)
  const details = [rarity, cardType, color, cardValue + '€'].filter(Boolean).join(' • ')
  return details ? `${index + 1}. ${name} (${details})` : `${index + 1}. ${name}`
}

export function getCandidateImage(cardData) {
  return normalizeApiString(
    cardData?.card_image ||
    cardData?.image ||
    cardData?.image_url ||
    cardData?.images?.small ||
    cardData?.images?.thumbnail ||
    cardData?.images?.large,
  )
}

export function applyFetchedCardDataToForm(cardData, targetCard, setType, options = {}) {
  const { preserveApiIds = false } = options
  const cardName = normalizeApiString(cardData.card_name || cardData.name)
  const cardColor = normalizeApiString(cardData.card_color || cardData.color)
  const cardType = normalizeApiString(cardData.card_type || cardData.type)
  const rarity = normalizeApiString(cardData.rarity)
  const cardNumber = normalizeApiCardNumber(cardData, targetCard.card_number)
  const userCardNumber = String(targetCard.card_number || '').trim()
  const setId = normalizeApiSetId(
    cardData.card_set_id || cardData.set_id || cardData.set,
    setType,
    userCardNumber || cardNumber,
  )
  const cardId = normalizeApiCardId(cardData.card_set_id || cardData.card_id)
  const fruit = normalizeApiString(cardData.fruit)
  const inventoryPrice = normalizeApiPrice(cardData.inventory_price ?? cardData.inventoryPrice ?? cardData.low_price)
  const marketPrice = normalizeApiPrice(cardData.market_price ?? cardData.marketPrice ?? cardData.price)
  const cardImage = normalizeApiString(
    cardData.card_image || cardData.image || cardData.image_url ||
    cardData.images?.small || cardData.images?.thumbnail || cardData.images?.large,
  )
  if (cardName) targetCard.card_name = cardName
  if (cardColor) targetCard.card_color = cardColor
  if (cardType) targetCard.card_type = cardType
  if (rarity) targetCard.rarity = rarity
  if (setId) targetCard.set_id = setId
  if (preserveApiIds) {
    const recomposed = composeCardId(setType, setId, cardNumber)
    targetCard.card_id = recomposed || cardId || ''
  }
  if (cardNumber) targetCard.card_number = cardNumber
  if (fruit) targetCard.fruit = fruit
  if (inventoryPrice !== '') targetCard.inventory_price = inventoryPrice
  if (marketPrice !== '') targetCard.market_price = marketPrice
  if (cardImage) targetCard.card_image = cardImage
  if (setType) targetCard.set_type = setType
}

export function isFresh(card) {
  if (!card.fetched_timestamp) return false
  return (Date.now() - new Date(card.fetched_timestamp).getTime()) < 24 * 60 * 60 * 1000
}

export function isFreshCt(card) {
  if (!card.cardtrader_market_price_fetched_at) return false
  return (Date.now() - new Date(card.cardtrader_market_price_fetched_at).getTime()) < 6 * 60 * 60 * 1000
}

export function bestMatchByName(localName, candidates) {
  const normalize = s => (s || '').toLowerCase().trim()
  const target = normalize(localName)
  if (!target) return candidates[0]
  const exact = candidates.find(c => normalize(c.card_name || c.name) === target)
  if (exact) return exact
  const partial = candidates.find(c =>
    normalize(c.card_name || c.name).includes(target) || target.includes(normalize(c.card_name || c.name)),
  )
  return partial ?? candidates[0]
}
